import { useEffect, useState } from 'react';

import { Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { assistantApis } from '../../../apis/assistantApis';
import { instanceApis } from '../../../apis/instanceApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import SettingsTabs from '../../../components/page-sections/settings/SettingsTabs';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { PRIVATE_ROUTE, buildPath } from '../../../routes/routes';
import { sentenceCase } from '../../../utils/utils';

const AssistantSettingsPage = () => {
  const navigate = useNavigate();
  const { projectId, assistantId } = useParams();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assistant, setAssistant] = useState(null);

  const fetchInstance = async () => {
    setError(false);
    try {
      const { data: assistantRes } = await assistantApis.getAssistantInstance(
        projectId,
        assistantId
      );
      setAssistant(assistantRes);
    } catch (error) {
      setError(true);
      showToast(
        TOAST_TYPE.ERROR,
        'An error occurred fetching your instance details.'
      );
    }
  };

  useEffect(() => {
    const onPageLoad = async () => {
      setLoading(true);
      await fetchInstance();
      setLoading(false);
    };

    onPageLoad();
  }, []);

  const handleDeleteInstance = async () => {
    try {
      await assistantApis.deleteAssistantInstance(projectId, assistantId);

      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('instance', { ns: 'instance' }),
          })
        )
      );
      const projectOverview = buildPath({
        path: PRIVATE_ROUTE.PROJECT_OVERVIEW,
        params: { projectId: projectId },
      });
      setTimeout(() => navigate(projectOverview), 800);
    } catch (errors) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('server', { ns: 'gps' }),
          })
        )
      );
    }
  };

  const handleRemoveImage = async () => {
    await instanceApis.deleteInstanceImage(projectId, assistantId);
    await fetchInstance();
  };

  const handleUpdateCidrs = async (cidrList) => {
    const requestBody = {
      name: assistant.name,
      description: assistant.description,
      subdomain: assistant.subdomain,
      clusterType: assistant.clusterType,
      cidrs: cidrList,
    };

    await assistantApis.updateAssistantInstance(
      projectId,
      assistantId,
      requestBody
    );
    await fetchInstance();
  };

  const handleUpdateInstance = async (data) => {
    if (data.icon) {
      const { icon, ...form } = data;
      const formData = new FormData();
      formData.append('image', icon);
      await instanceApis.updateInstanceImage(projectId, assistantId, formData);
      await assistantApis.updateAssistantInstance(projectId, assistantId, form);
    } else {
      await assistantApis.updateAssistantInstance(projectId, assistantId, data);
    }
    await fetchInstance();
  };

  if (loading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={t('fetch', {
          ns: 'errors',
          content: t('instance', { ns: 'instance' }),
        })}
      />
    );

  return (
    <Container>
      <Breadcrumbs assistantName={assistant.name} />
      <TitlePanel title={t('settings', { ns: 'titles' })} />
      <SettingsTabs
        instance={assistant}
        onDeleteInstance={handleDeleteInstance}
        onRemoveImage={handleRemoveImage}
        onUpdateCidrs={handleUpdateCidrs}
        onUpdateInstance={handleUpdateInstance}
      />
    </Container>
  );
};

export default AssistantSettingsPage;
